import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import toast from 'react-hot-toast';
import api from '../services/api';

const initial = { enabled:false, publicUrl:'', clinicName:'Clinic', clinicContact:'', defaultDurationMinutes:30, maximumAdvanceDays:60, minimumNoticeMinutes:120, allowRescheduling:false, rescheduleCutoffMinutes:1440, bookingInstructions:'', recentBookings:[] };
const databaseUnavailableMessage = 'Patient Self-Booking setup is not available because the local database has not been updated.';

export default function PatientSelfBookingSettings({ embedded = false }) {
  const [form, setForm] = useState(initial);
  const [qr, setQr] = useState('');
  const [busy, setBusy] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savedEnabled, setSavedEnabled] = useState(false);
  const [message, setMessage] = useState({ type:'', text:'' });
  const [linkCopied, setLinkCopied] = useState(false);
  const copyResetTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get('/patient-self-booking/settings');
        if (!cancelled) {
          setForm({ ...initial, ...data });
          setSavedEnabled(Boolean(data.enabled));
        }
      } catch (error) {
        if (!cancelled) {
          const text = error?.response?.status >= 500 ? databaseUnavailableMessage : (error?.response?.data?.error || 'Settings could not be loaded.');
          setMessage({ type:'error', text });
          toast.error(text);
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => () => { if (copyResetTimer.current) window.clearTimeout(copyResetTimer.current); }, []);

  useEffect(() => {
    let active = true;
    if (!savedEnabled || !form.publicUrl) { setQr(''); return undefined; }
    QRCode.toDataURL(form.publicUrl, { width:360, margin:2 })
      .then((value) => active && setQr(value))
      .catch(() => active && setQr(''));
    return () => { active = false; };
  }, [form.publicUrl, savedEnabled]);

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setMessage({ type:'', text:'' });
    try {
      await api.put('/patient-self-booking/settings', form);
      const { data } = await api.get('/patient-self-booking/settings');
      setForm({ ...initial, ...data });
      setSavedEnabled(Boolean(data.enabled));
      const text = data.enabled ? 'Patient Self-Booking has been enabled.' : 'Patient Self-Booking settings saved.';
      setMessage({ type:'success', text });
      toast.success(text);
    } catch (error) {
      const text = error?.response?.status >= 500 ? databaseUnavailableMessage : (error?.response?.data?.error || 'Settings could not be saved.');
      setMessage({ type:'error', text });
      toast.error(text);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    const fallbackCopy = () => {
      const textarea = document.createElement('textarea');
      textarea.value = form.publicUrl;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand('copy');
      textarea.remove();
      if (!copied) throw new Error('Copy command was rejected');
    };
    try {
      if (navigator.clipboard?.writeText) {
        try { await navigator.clipboard.writeText(form.publicUrl); } catch (_clipboardError) { fallbackCopy(); }
      } else fallbackCopy();
      setLinkCopied(true);
      setMessage({ type:'success', text:'Booking link copied.' });
      if (copyResetTimer.current) window.clearTimeout(copyResetTimer.current);
      copyResetTimer.current = window.setTimeout(() => setLinkCopied(false), 2200);
      toast.success('Booking link copied.');
    } catch (_error) {
      const text = 'The booking link could not be copied. Please copy it manually.';
      setMessage({ type:'error', text });
      toast.error(text);
    }
  };
  const share = () => {
    const text = `Book your appointment with ${form.clinicName || 'our clinic'} using this link:\n\n${form.publicUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };
  const openBookingPage = () => window.open(form.publicUrl, '_blank', 'noopener,noreferrer');
  const download = () => {
    const anchor = document.createElement('a');
    anchor.href = qr;
    anchor.download = 'patient-self-booking-qr.png';
    anchor.click();
  };

  const available = savedEnabled && Boolean(form.publicUrl);
  if (busy && !form.publicUrl && !message.text) return <div className="p-8 text-center">Loading Patient Self-Booking…</div>;

  return <div className={embedded ? 'space-y-5' : 'min-h-screen bg-slate-50 p-4 sm:p-8'}>
    <section className={embedded ? 'space-y-5' : 'mx-auto max-w-5xl space-y-5'}>
      {message.text ? <div role={message.type === 'error' ? 'alert' : 'status'} className={`rounded-2xl border p-4 text-sm font-semibold ${message.type === 'error' ? 'border-rose-200 bg-rose-50 text-rose-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>{message.text}</div> : null}
      <label className="flex items-center gap-3 rounded-2xl border bg-white p-4 font-bold">
        <input type="checkbox" checked={form.enabled} onChange={(event) => setForm({ ...form, enabled:event.target.checked })} className="h-5 w-5" />
        Enable Self-Booking
      </label>
      {!available ? <div className="rounded-2xl border border-dashed border-teal-300 bg-teal-50 p-5 text-sm font-semibold text-teal-900">Enable Patient Self-Booking and save your settings to generate your clinic booking link and QR code.</div> : null}
      <div className="grid gap-6 rounded-3xl bg-white p-5 shadow-sm lg:grid-cols-[minmax(0,1fr)_280px] lg:p-6">
        <div className="space-y-4">
          <Field label="Booking Link"><div className="flex flex-col gap-2 sm:flex-row"><input aria-label="Booking Link" readOnly value={available ? form.publicUrl : ''} placeholder="Available after self-booking is enabled and saved" className="min-w-0 flex-1 rounded-xl border bg-slate-50 p-3 text-sm" /><button type="button" disabled={!available} onClick={copy} aria-live="polite" className={`min-w-[116px] rounded-xl border px-4 py-3 font-bold shadow-sm transition hover:border-teal-300 hover:bg-teal-50 active:translate-y-px active:bg-teal-100 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal-100 disabled:cursor-not-allowed disabled:opacity-50 ${linkCopied ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-800'}`}>{linkCopied ? 'Copied ✓' : 'Copy Link'}</button></div></Field>
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={!available} onClick={share} className="rounded-xl bg-emerald-600 px-4 py-2 font-bold text-white disabled:opacity-50">Send on WhatsApp</button>
            <button type="button" disabled={!available} onClick={openBookingPage} className="rounded-xl border px-4 py-2 font-bold disabled:opacity-50">Preview Patient Booking Page</button>
          </div>
          <button type="button" onClick={() => setSettingsOpen((value) => !value)} className="rounded-xl border px-4 py-2 font-bold" aria-expanded={settingsOpen}>Booking Settings</button>
          {settingsOpen ? <div className="grid grid-cols-1 gap-x-5 gap-y-6 rounded-2xl border bg-slate-50 p-4 md:grid-cols-2 md:p-5">
            <UnitNumber label="Default Appointment Duration" suffix="Minutes" min="1" step="1" value={form.defaultDurationMinutes} set={(value) => setForm({ ...form, defaultDurationMinutes:value })} />
            <UnitNumber label="Maximum Days Bookable in Advance" suffix="Days" min="1" step="1" value={form.maximumAdvanceDays} set={(value) => setForm({ ...form, maximumAdvanceDays:value })} />
            <Hours label="Minimum Booking Notice" suffix="Hours" valueMinutes={form.minimumNoticeMinutes} setMinutes={(value) => setForm({ ...form, minimumNoticeMinutes:value })} helper="Patients cannot book within this time before the appointment." />
            <Hours label="Patient Rescheduling Cutoff" suffix="Hours" valueMinutes={form.rescheduleCutoffMinutes} setMinutes={(value) => setForm({ ...form, rescheduleCutoffMinutes:value })} helper="Patients cannot reschedule within this time before the appointment." />
            <label className="flex min-h-12 items-center gap-3 rounded-xl border bg-white px-4 py-3 font-bold md:col-span-2"><input type="checkbox" checked={form.allowRescheduling} onChange={(event) => setForm({ ...form, allowRescheduling:event.target.checked })} className="h-5 w-5" />Allow Patient Rescheduling</label>
            <div className="md:col-span-2"><Field label="Optional Booking Instructions"><textarea maxLength="1000" value={form.bookingInstructions} onChange={(event) => setForm({ ...form, bookingInstructions:event.target.value })} placeholder="Example: Please arrive 10 minutes early. Bring previous prescriptions or X-rays if available." className="min-h-32 w-full resize-y rounded-xl border p-3 leading-6" /><span className="mt-1 block text-right text-xs font-normal text-slate-400">{form.bookingInstructions.length}/1000</span></Field></div>
          </div> : null}
          <button type="button" disabled={busy} onClick={save} className="rounded-xl bg-teal-700 px-6 py-3 font-bold text-white disabled:bg-slate-300">{busy ? 'Saving Settings…' : 'Save Settings'}</button>
        </div>
        <aside className="rounded-2xl bg-slate-50 p-4 text-center"><h2 className="font-bold">QR Code</h2>{qr ? <img src={qr} alt="Patient Self-Booking QR code" className="mx-auto mt-3 w-full" /> : <div className="mt-3 flex min-h-48 items-center justify-center rounded-xl border border-dashed bg-white p-4 text-sm text-slate-500">QR code will appear after self-booking is enabled and saved.</div>}<button type="button" onClick={download} disabled={!qr} className="mt-4 w-full rounded-xl border bg-white p-2 font-bold disabled:opacity-50">Download QR</button></aside>
      </div>
      <section className="rounded-3xl bg-white p-5 shadow-sm"><h2 className="text-xl font-bold">Recent Self-Bookings</h2>{form.recentBookings.length ? <div className="mt-4 space-y-2">{form.recentBookings.map((booking, index) => <a href="/dashboard" key={booking.id || index} className="flex flex-wrap justify-between gap-2 rounded-xl border p-3 text-sm"><b>{booking.patientName}</b><span>{booking.date} · {booking.time}</span><b className="text-teal-700">{booking.rescheduled ? 'Patient Rescheduled' : (booking.status || 'Patient Self-Booked')}</b></a>)}</div> : <p className="mt-3 text-sm text-slate-500">No patient self-bookings yet.</p>}</section>
    </section>
  </div>;
}

function Field({ label, children }) { return <label className="block text-sm font-bold">{label}<div className="mt-2">{children}</div></label>; }
function UnitNumber({ label, suffix, value, set, min = '0', step = '1', helper }) { return <Field label={label}><div className="flex min-h-12 min-w-0 w-full max-w-full items-stretch rounded-xl border bg-white focus-within:border-teal-600 focus-within:ring-2 focus-within:ring-teal-100"><input aria-label={label} type="number" min={min} step={step} value={value} onChange={(event) => set(Number(event.target.value))} className="min-w-0 flex-1 rounded-l-xl border-0 bg-transparent px-3 py-2 outline-none" /><span className="flex min-w-[5.5rem] flex-none items-center justify-center whitespace-nowrap rounded-r-xl border-l bg-slate-50 px-2 text-sm font-semibold text-slate-600">{suffix}</span></div>{helper ? <p className="mt-2 text-xs font-normal leading-5 text-slate-500">{helper}</p> : null}</Field>; }
function Hours({ label, suffix, valueMinutes, setMinutes, helper }) { return <UnitNumber label={label} suffix={suffix} value={Number(valueMinutes || 0) / 60} set={(value) => setMinutes(Math.round(value * 60))} min="0" step="0.5" helper={helper} />; }
